import { useEffect, useState } from "react";
import Header from "../components/organisms/Header";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import Table from "../components/organisms/tables/Table";
import { docListHeaders } from "../assets/data/headers";
import Welcome from "../components/molecules/Welcome";
import DashboardCard from "../components/atoms/DashboardCard";
import { useHospDocData } from "../lib/contexts/HospitalDoctorContext";
import Loader from "../components/molecules/Loader";
import { getRouteSegment } from "../lib/utils/funcs";
import { useUserData } from "../lib/contexts/UserContext";
import { getDoctorListByHospitalId } from "../lib/apis/doctor";
import BarGraph from "../components/organisms/charts/BarGraph";
import LineGraph from "../components/organisms/charts/LineGraph";
import DarkBlueButton from "../components/atoms/buttons/DarkBlueButton";
import WhiteButton from "../components/atoms/buttons/WhiteButton";
import DashboardCard2 from "../components/atoms/DashboardCard2";
import { MdOutlineMoreTime } from "react-icons/md";
import AOS from "aos";
import "aos/dist/aos.css";
import { toast } from "react-toastify";
import { getCookie, setCookie } from "../lib/utils/cookies";
import apiClient from "../lib/apis/fetch";
import { baseURL } from "../lib/utils/constants";
import SubscriptionPopUp from "../components/molecules/SubscriptionPopUp";
import { getSettingsByHospitalId } from "../lib/apis/adminpanel";
import FeaturePromotionPopUp from "../components/molecules/FeaturePromotionPopUp";
// ..
AOS.init();

const Dashboard = () => {
  const navigate = useNavigate();
  const { userData } = useUserData();
  const [hasShownNetworkWarning, setHasShownNetworkWarning] = useState(false);

  const showNetworkWarning = () => {
    if (!hasShownNetworkWarning) {
      toast.warning("Network is slow. Please check your internet connection.");
      setHasShownNetworkWarning(true);
    }
  };

  const currnetYear = new Date().getFullYear().toString();

  const { hospitalID, setHospitalID, doctorsData, setDoctorsData } =
    useHospDocData();

  const id = getRouteSegment(1);
  const [customBtn, setCustomBtn] = useState(false);
  const [load, setLoad] = useState(true);
  const [customDates, setCustomDates] = useState({
    startDate: "",
    endDate: "",
    startYear: currnetYear,
    endYear: currnetYear,
  });
  const hospital_id = getRouteSegment(1);
  const [availableDocCount, setAvailableDocCount] = useState();

  const getData = async (formattedDate: string) => {
    const startTime = performance.now();
    try {
      const currentDate = new Date();
      const pastDate = new Date(currentDate);
      pastDate.setMonth(pastDate.getMonth() - 11);
      pastDate.setDate(1);
      const response = await apiClient.get(`${baseURL}/analytics/booking`, {
        params: {
          hospital_id: hospital_id,
          start_date: pastDate.toISOString().split("T")[0],
          end_date: formattedDate,
          doctor_id: userData?.doctor_id,
        },
      });
      const endTime = performance.now();
      const responseTime = endTime - startTime;

      if (responseTime > 3000) {
        showNetworkWarning();
      }

      if (response?.status === 200) {
        setCookie("bookingOverview", JSON.stringify(response.data.result), 30);
        setWaitingTime({
          then: response.data.result.avg_wait_time_last_50_minutes,
          now: response.data.result.avg_wait_time_first_50_minutes,
        });
        setTimeout(() => {
          setLoad(false);
        }, 500);
      }
      return response;
    } catch (error) {
      toast.error("Failed to fetch booking data. Please try again.");
      setLoad(false);
    }
  };

  const getAvgWaitTime = async () => {
    const startTime = performance.now();
    try {
      const response = await apiClient.get(
        `${baseURL}/analytics/waitTimeStats`,
        {
          params: {
            hospital_id: hospital_id,
            doctor_id: userData?.doctor_id,
          },
        }
      );
      const endTime = performance.now();
      const responseTime = endTime - startTime;

      if (response?.status === 200) {
        setCookie("avgWaitTimeStats", JSON.stringify(response.data.result), 1);
        setAvgWaitTime(response.data.result);
        setTimeout(() => {
          setLoad(false);
        }, 500);
      }
    } catch (error) {
      setLoad(false);
    }
  };

  const getTreatmentTime = async () => {
    try {
      const response = await apiClient.get(
        `${baseURL}/analytics/totalTreatmentTimeStats`,
        {
          params: {
            doctor_id: userData?.doctor_id,
          },
        }
      );

      if (response?.status === 200) {
        setCookie(
          "totalTreatmentTimeStats",
          JSON.stringify(response.data.result),
          1
        );
        setTotalTreatmentTimeStats(response.data.result);
        setTimeout(() => {
          setLoad(false);
        }, 500);
      }
    } catch (error) {
      setLoad(false);
    }
  };

  const getDailyData = async () => {
    const startTime = performance.now();
    try {
      const response = await apiClient.get(
        `${baseURL}/analytics/current-month`,
        {
          params: {
            hospital_id: hospital_id,
            doctor_id: userData?.doctor_id,
          },
        }
      );
      const endTime = performance.now();
      const responseTime = endTime - startTime;

      if (responseTime > 3000) {
        showNetworkWarning();
      }

      if (response?.status === 200) {
        setCookie("dailyOverview", JSON.stringify(response.data.result), 1);
        setAvailableDocCount(response.data.result?.available_doc_count);
        setTimeout(() => {
          setLoad(false);
        }, 500);
      }
      return response;
    } catch (error) {
      toast.error("Failed to fetch daily data. Please try again.");
      setLoad(false);
    }
  };
  const getTimezone = async () => {
    const startTime = performance.now();
    const response = await getSettingsByHospitalId(hospitalID || "");
    const endTime = performance.now();
    const responseTime = endTime - startTime;

    if (responseTime > 3000) {
      showNetworkWarning();
    }

    if (response.status === 200) {
      setCookie("timezone", response.data.result[0].timezone, 7);
    }
  };
  useEffect(() => {
    // Get today's date in "YYYY-MM-DD" format
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;
    // Create a "MM/YYYY" string for comparison with bookingOverview
    const currentMonthYear = `${month}/${year}`;

    // BookingOverview logic
    const bookingCookie = getCookie("bookingOverview");
    if (bookingCookie) {
      try {
        const bookingData = JSON.parse(bookingCookie);
        const monthlyBookings = bookingData.monthly_bookings;
        const lastBookingMonth =
          monthlyBookings && monthlyBookings.length > 0
            ? monthlyBookings[monthlyBookings.length - 1].month
            : null;
        if (!lastBookingMonth || lastBookingMonth !== currentMonthYear) {
          // Fetch new booking data if the cookie is missing or the month is outdated
          getData(formattedDate);
        } else {
          setWaitingTime({
            then: bookingData.avg_wait_time_last_50_minutes,
            now: bookingData.avg_wait_time_first_50_minutes,
          });
        }
      } catch (error) {
        console.error("Error parsing bookingOverview cookie", error);
        getData(formattedDate);
      }
    } else {
      getData(formattedDate);
    }

    // DailyOverview logic
    const dailyCookie = getCookie("dailyOverview");
    if (dailyCookie) {
      try {
        const dailyData = JSON.parse(dailyCookie);
        const dailyCounts = dailyData.daily_counts;
        const lastDay =
          dailyCounts && dailyCounts.length > 0
            ? dailyCounts[dailyCounts.length - 1].day
            : null;
        if (!lastDay || lastDay !== formattedDate) {
          getDailyData();
        }
      } catch (error) {
        console.error("Error parsing dailyOverview cookie", error);
        getDailyData();
      }
    } else {
      getDailyData();
    }

    // Average Waiting Time Stats logic
    if (userData?.role === "Doctor") {
      const treatmentTimeCookie = getCookie("totalTreatmentTimeStats");
      if (!treatmentTimeCookie) {
        getTreatmentTime();
      } else {
        try {
          const treatmentData = JSON.parse(treatmentTimeCookie);
          setTotalTreatmentTimeStats(treatmentData);
          setTimeout(() => {
            setLoad(false);
          }, 500);
        } catch (error) {
          console.error("Error parsing totalTreatmentTimeStats cookie", error);
          getTreatmentTime();
        }
      }
    } else {
      const avgWaitTimeCookie = getCookie("avgWaitTimeStats");
      if (!avgWaitTimeCookie) {
        getAvgWaitTime();
      } else {
        try {
          const avgData = JSON.parse(avgWaitTimeCookie);
          setAvgWaitTime(avgData);
          setTimeout(() => {
            setLoad(false);
          }, 500);
        } catch (error) {
          console.error("Error parsing avgWaitTimeStats cookie", error);
          getAvgWaitTime();
        }
      }
    }
  }, [hospital_id, userData?.role]);

  const [overviewStartMonth, setOverviewStartMonth] = useState("");
  const [overviewEndMonth, setOverviewEndMonth] = useState("");
  const [overviewStartYear, setOverviewStartYear] = useState(currnetYear);
  const [overviewEndYear, setOverviewEndYear] = useState(currnetYear);
  const [overview, setOverview] = useState({
    confirm: true,
    type: "monthly",
  });
  const [noshow, setNoShow] = useState("month");
  const [waitingTime, setWaitingTime] = useState({
    then: "0",
    now: "0",
  });
  const [avgWaitTime, setAvgWaitTime] = useState({
    avg_wait_time_minutes: 0,
    highest_avg_wait_time_minutes: 0,
    lowest_avg_wait_time_minutes: 0,
  });

  const [totalTreatmentTimeStats, setTotalTreatmentTimeStats] = useState({
    status_diff_max: 0,
    status_diff_min: 0,
    status_diff_avg: 0,
  });
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Helper function to create custom date range
  const createCustomDateRange = (
    startMonth: string,
    endMonth: string,
    startYear: string,
    endYear: string
  ) => {
    const startMonthIndex = monthNames.indexOf(startMonth);
    const endMonthIndex = monthNames.indexOf(endMonth);

    const startMonthFormatted = String(startMonthIndex + 1).padStart(2, "0");
    const endMonthFormatted = String(endMonthIndex + 1).padStart(2, "0");

    // Get last day of end month
    const endDate = new Date(Number(endYear), endMonthIndex + 1, 0);
    const lastDayOfMonth = String(endDate.getDate()).padStart(2, "0");

    return {
      startDate: `${startYear}-${startMonthFormatted}-01`,
      endDate: `${endYear}-${endMonthFormatted}-${lastDayOfMonth}`,
      startYear: startYear,
      endYear: endYear,
    };
  };

  useEffect(() => {
    setHospitalID(id);
    getTimezone();
  }, [id]);

  useEffect(() => {
    const fetchdoctorsData = async () => {
      const res = await getDoctorListByHospitalId(hospitalID);
      setDoctorsData(undefined);
      if (res?.status === 200) {
        console.log("doctorsData", res.data.result);
        setDoctorsData(res.data.result);
      } else console.log(res);
    };

    fetchdoctorsData();
  }, [hospitalID, userData]);
  console.log(waitingTime);

  const [isSubscriptionExpired, setIsSubscriptionExpired] = useState(true); // State for Subscription Modal

  // Function to check subscription status
  const checkSubscriptionStatus = async () => {
    try {
      const response = await apiClient.get(
        `${baseURL}/user/subscription-status`,
        {
          params: { hospital_id: hospital_id },
        }
      );

      if (response?.status === 200 && response.data.is_expired) {
        setIsSubscriptionExpired(true); // Open modal if subscription expired
      }
    } catch (error) {
      console.error("Error checking subscription:", error);
      setIsSubscriptionExpired(true); // Default to expired in case of an error
    }
  };

  useEffect(() => {
    setHospitalID(id);
    // checkSubscriptionStatus(); // Check subscription status on page load
  }, [id]);

  // Reset warning state when hospital changes
  useEffect(() => {
    setHasShownNetworkWarning(false);
  }, [hospital_id]);

  const [showFeaturePopup, setShowFeaturePopup] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("featurePopupShown")) {
      setShowFeaturePopup(true);
    }
  }, []);

  const handleCloseFeaturePopup = () => {
    setShowFeaturePopup(false);
    localStorage.setItem("featurePopupShown", "true");
  };

  const handleExploreFeaturePopup = () => {
    setShowFeaturePopup(false);
    localStorage.setItem("featurePopupShown", "true");
    navigate(`/${hospital_id}/createprescription`);
  };

  return (
    <div className="flex flex-col w-full h-[100vh] overflow-y-auto side_bar">
      {/* <FeaturePromotionPopUp
        isOpen={showFeaturePopup}
        onClose={handleCloseFeaturePopup}
        onExplore={handleExploreFeaturePopup}
        title="Prescribe with One Click"
        description={
          <div>
            <p className="mb-4 text-gray-700">
              Create and send prescriptions easily with our new Prescription
              Template Creation feature. Try it now and enjoy:
            </p>
            <p className="mb-2 text-gray-700">
              👨‍⚕️ <b>Seamless prescription workflow</b>
              <br />
              Quickly generate and send prescriptions right after appointments —
              no extra steps.
            </p>
            <p className="mb-2 text-gray-700">
              📝 <b>Customizable templates</b>
              <br />
              Build and save templates to match your practice's needs and
              branding.
            </p>
            <p className="mb-2 text-gray-700">
              🚀 <b>Faster follow-up care</b>
              <br />
              Deliver clear, professional prescriptions to patients instantly —
              improving care and efficiency.
            </p>
          </div>
        }
        youtubeUrl="https://www.youtube.com/embed/1S1t3BCdJfI?si=YggHgH7zZ274fhOj"
        exploreText="Try it now – it's free to explore"
        remindLater="Remind me later"
        handleReminderLater={() => {
          setShowFeaturePopup(false);
        }}
      /> */}
      <SubscriptionPopUp
        isOpen={isSubscriptionExpired}
        onClose={() => setIsSubscriptionExpired(false)}
        hospitalId={hospital_id}
      />

      {!load && userData !== undefined && (
        <>
          <Header header value={"Dashboard"} />
          <div className="flex flex-col lg:flex-row mt-7 px-7 lg:pr-0 mb-5">
            <div className="flex flex-col w-full lg:w-1/2 justify-between gap-5">
              <Welcome />
              <div
                className="flex bg-[#335FE4] rounded-lg p-2 text-white gap-4"
                data-aos="zoom-in"
                data-aos-duration="1000"
              >
                {/* Begin: Doctor vs Non-Doctor Card */}
                {userData.role !== "Doctor" ? (
                  <>
                    <div className="flex justify-end w-[38%] p-2 xl:p-6">
                      <div
                        className="flex flex-col justify-center items-end"
                        data-aos="slide-left"
                        data-aos-duration="1000"
                      >
                        <p className="font-thin text-sm md:text-md">
                          Highest Avg
                        </p>
                        <h1 className="font-normal text-lg md:text-3xl xl:text-4xl mt-1 text-right">
                          {(+avgWaitTime.highest_avg_wait_time_minutes).toFixed(
                            2
                          )}{" "}
                          min
                        </h1>
                      </div>
                    </div>
                    <div className="flex justify-center w-[40%] md:w-[24%] bg-[#2651d1] rounded-lg p-4 z-20">
                      <div className="flex flex-col justify-center items-center">
                        <MdOutlineMoreTime className="w-8 h-8" />
                        <p className="text-xs mt-2 font-thin text-center">
                          Queue waiting time
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-start w-[38%] p-2 xl:p-6">
                      <div
                        className="flex flex-col justify-center items-start"
                        data-aos="slide-right"
                        data-aos-duration="1000"
                      >
                        <p className="font-thin  text-sm md:text-md">
                          Lowest Avg
                        </p>
                        <h1 className="font-normal text-lg md:text-3xl xl:text-4xl mt-1">
                          {(+avgWaitTime.lowest_avg_wait_time_minutes).toFixed(
                            2
                          )}{" "}
                          min
                        </h1>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-end w-[38%] p-2 xl:p-6">
                      <div
                        className="flex flex-col justify-center items-end"
                        data-aos="slide-left"
                        data-aos-duration="1000"
                      >
                        <p className="font-thin text-sm md:text-md">Max</p>
                        <h1 className="font-normal text-lg md:text-3xl xl:text-4xl mt-1 text-right">
                          {(+totalTreatmentTimeStats.status_diff_avg).toFixed(
                            2
                          )}{" "}
                          min
                        </h1>
                      </div>
                    </div>
                    <div className="flex justify-center w-[40%] md:w-[24%] bg-[#2651d1] rounded-lg p-4 z-20">
                      <div className="flex flex-col justify-center items-center">
                        <MdOutlineMoreTime className="w-8 h-8" />
                        <p className="text-xs mt-2 font-thin text-center">
                          Treatment time
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-start w-[38%] p-2 xl:p-6">
                      <div
                        className="flex flex-col justify-center items-start"
                        data-aos="slide-right"
                        data-aos-duration="1000"
                      >
                        <p className="font-thin  text-sm md:text-md">Min</p>
                        <h1 className="font-normal text-lg md:text-3xl xl:text-4xl mt-1">
                          {(+totalTreatmentTimeStats.status_diff_min).toFixed(
                            2
                          )}{" "}
                          min
                        </h1>
                      </div>
                    </div>
                  </>
                )}
                {/* End: Doctor vs Non-Doctor Card */}
              </div>
              <div className="flex flex-col">
                <div className="flex flex-col md:flex-row space-y-[10px] md:space-x-[10px] md:space-y-0">
                  <DashboardCard
                    hospitalID={String(hospitalID)}
                    doctor_id={userData?.doctor_id}
                    name={"Today's Bookings"}
                  />
                  <DashboardCard2
                    value={
                      doctorsData === undefined
                        ? "0"
                        : String(doctorsData?.length)
                    }
                    availableDocCount={
                      availableDocCount === undefined
                        ? doctorsData === undefined
                          ? "0"
                          : String(doctorsData?.length)
                        : String(availableDocCount)
                    }
                    role={userData.role}
                    hospitalID={hospitalID}
                    doctorId={userData.doctor_id}
                    name={"Doctors available today"}
                  />
                </div>
              </div>
            </div>
            {userData.role !== "Doctor" ? (
              <div className="lg:ml-5 lg:mr-7 lg:w-1/2 bg-white rounded-lg overflow-hidden mt-6 lg:mt-0 flex flex-col h-full">
                <div className="flex flex-row justify-between items-center bg-white px-3 md:px-5 py-4 rounded-lg">
                  <p className="text-dark text-sm lg:text-base font-bold">
                    Doctor List
                  </p>

                  <WhiteButton
                    name=" View All"
                    className="hover:bg-black hover:text-white text-white  bg-black opacity-100 w-24 text-sm mr-5"
                    handleClick={() => navigate("/" + hospitalID + "/doctors")}
                  />
                </div>
                <div></div>
                {doctorsData === undefined ? (
                  <div className="bg-white flex justify-center items-center !w-full h-40">
                    <p className="text-center">No Doctors Found.</p>
                  </div>
                ) : (
                  <div className="!w-full overflow-x-auto flex-1 min-h-0">
                    <div className="h-full !w-full overflow-y-auto">
                      <Table
                        type="doclist"
                        headers={docListHeaders}
                        docData={doctorsData
                          .sort((a, b) => {
                            // Sort by is_active first (true values first)
                            if (a.is_active !== b.is_active) {
                              return b.is_active ? 1 : -1;
                            }
                            // Then sort by created_at date
                            return (
                              moment(a.created_at).valueOf() -
                              moment(b.created_at).valueOf()
                            );
                          })
                          .slice(0, 5)}
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="lg:ml-5 lg:mr-7 lg:w-1/2 bg-white rounded-lg overflow-hidden mt-6 lg:mt-0 flex flex-col h-full p-4">
                <div className="flex flex-col lg:flex-row md:justify-between pl-4 items-center lg:items-center">
                  <h1 className="font-semibold text-xl">No shows overview</h1>
                  <div className="flex gap-2 mt-4 lg:mt-0 w-full md:w-auto">
                    <WhiteButton
                      name="Monthly"
                      className={
                        noshow === "month"
                          ? "bg-black hover:bg-black text-white  opacity-100 w-full md:w-20 !px-0"
                          : "hover:bg-black hover:text-white  bg-white opacity-100  w-full md:w-20 !px-0"
                      }
                      handleClick={() => {
                        setNoShow("month");
                      }}
                    />
                    <WhiteButton
                      name="Daily"
                      className={
                        noshow === "daily"
                          ? "bg-black hover:bg-black text-white  opacity-100 w-full md:w-20 !px-0"
                          : "hover:bg-black hover:text-white  bg-white opacity-100  w-full md:w-20 !px-0"
                      }
                      handleClick={() => {
                        setNoShow("daily");
                      }}
                    />
                  </div>
                </div>
                <BarGraph type={noshow} doctor_id={userData.doctor_id} />
              </div>
            )}
          </div>
          <div className="px-7 flex flex-col lg:flex-row justify-between gap-5 mb-4 mt-8 lg:mt-0">
            <div
              className={`w-full ${
                userData.role === "Doctor" ? "lg:w-full" : "lg:w-1/2"
              } bg-white rounded-lg px-4 py-5 lg:px-5 lg:py-6 pb-2 lg:pb-2`}
            >
              <div className="flex flex-col md:flex-row justify-between pl-4 items-center">
                <h1 className="font-semibold text-xl">Bookings overview</h1>
                <div className="flex gap-0 mt-4 lg:mt-0 w-full md:w-auto">
                  <WhiteButton
                    name="Custom"
                    className={
                      customBtn === true || overview.type === "custom"
                        ? "bg-black hover:bg-black text-white  opacity-100 w-full md:w-28 !md:px-0"
                        : "hover:bg-black hover:text-white  bg-white opacity-100  w-full md:w-28 !md:px-0"
                    }
                    handleClick={() => {
                      setCustomBtn(true);
                      setOverview({
                        ...overview,
                        type: "custom",
                      });
                    }}
                  />
                  <WhiteButton
                    name="Daily"
                    className={
                      overview.type === "daily"
                        ? "bg-black hover:bg-black text-white  opacity-100 w-full md:w-20 !px-0"
                        : "hover:bg-black hover:text-white  bg-white opacity-100  w-full md:w-20 !px-0"
                    }
                    handleClick={() => {
                      setOverview({
                        ...overview,
                        type: "daily",
                      });
                      setCustomBtn(false);
                    }}
                  />
                  <WhiteButton
                    name="Monthly"
                    className={
                      overview.type === "monthly"
                        ? "bg-black hover:bg-black text-white  opacity-100 w-full md:w-20 !px-0"
                        : "hover:bg-black hover:text-white  bg-white opacity-100  w-full md:w-20 !px-0"
                    }
                    handleClick={() => {
                      setOverview({
                        ...overview,
                        type: "monthly",
                      });
                      setCustomBtn(false);
                    }}
                  />
                </div>
              </div>
              {customBtn && (
                <div className="p-4 flex flex-col items-center bg-[#FAFAFA] mt-4">
                  <h1 className="font-semibold">Choose custom overview</h1>

                  {/* ===== START YEAR ===== */}
                  <div className="w-full">
                    <p className="text-modalText text-md mb-1 mt-6 font-bold">
                      Choose Start Year
                    </p>
                    <div className="flex gap-2 mt-2">
                      {[
                        `${Number(currnetYear) - 2}`,
                        `${Number(currnetYear) - 1}`,
                        currnetYear,
                      ].map((year) => (
                        <WhiteButton
                          key={year}
                          name={year}
                          className={
                            (year === String(customDates.startYear) &&
                              overview.confirm === true) ||
                            (year === String(overviewStartYear) &&
                              overview.confirm === false)
                              ? "hover:bg-black text-white bg-black opacity-100"
                              : "hover:bg-black hover:text-white bg-white opacity-100"
                          }
                          handleClick={() => {
                            setOverview({ ...overview, confirm: false });
                            setOverviewStartYear(year);
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* ===== START MONTH ===== */}
                  <div className="w-full">
                    <p className="text-modalText text-md mb-1 mt-6 font-bold">
                      Choose Start Month
                    </p>
                    <div className="grid grid-cols-6 gap-4 mt-2">
                      {monthNames.map((month) => (
                        <WhiteButton
                          key={month}
                          name={month}
                          className={
                            (month ===
                              monthNames[
                                Number(customDates.startDate.split("-")[1]) - 1
                              ] &&
                              overview.confirm === true) ||
                            (month === overviewStartMonth &&
                              overview.confirm === false)
                              ? "bg-[#A3B9FF] text-white rounded-full border-none !p-1 !px-2 md:h-12 w-max md:w-20"
                              : "bg-transparent hover:bg-[#A3B9FF] hover:text-white rounded-full !p-1 !px-2 md:h-12 w-max md:w-20"
                          }
                          handleClick={() => {
                            setOverview({ ...overview, confirm: false });
                            setOverviewStartMonth(month);
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* ===== END YEAR ===== */}
                  <div className="w-full">
                    <p className="text-modalText text-md mb-1 mt-6 font-bold">
                      Choose End Year
                    </p>
                    <div className="flex gap-2 mt-2">
                      {[
                        `${Number(currnetYear) - 2}`,
                        `${Number(currnetYear) - 1}`,
                        currnetYear,
                      ].map((year) => (
                        <WhiteButton
                          key={year}
                          name={year}
                          className={
                            (year === String(customDates.endYear) &&
                              overview.confirm === true) ||
                            (year === String(overviewEndYear) &&
                              overview.confirm === false)
                              ? "hover:bg-black text-white bg-black opacity-100"
                              : "hover:bg-black hover:text-white bg-white opacity-100"
                          }
                          handleClick={() => {
                            setOverview({ ...overview, confirm: false });
                            setOverviewEndYear(year);
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* ===== END MONTH ===== */}
                  <div className="w-full">
                    <p className="text-modalText text-md mb-1 mt-6 font-bold">
                      Choose End Month
                    </p>
                    <div className="grid grid-cols-6 gap-4 mt-2">
                      {monthNames.map((month) => (
                        <WhiteButton
                          key={month}
                          name={month}
                          className={
                            (month ===
                              monthNames[
                                Number(customDates.endDate.split("-")[1]) - 1
                              ] &&
                              overview.confirm === true) ||
                            (month === overviewEndMonth &&
                              overview.confirm === false)
                              ? "bg-[#A3B9FF] text-white rounded-full border-none !p-1 !px-2 md:h-12 w-max md:w-20"
                              : "bg-transparent hover:bg-[#A3B9FF] hover:text-white rounded-full !p-1 !px-2 md:h-12 w-max md:w-20"
                          }
                          handleClick={() => {
                            setOverview({ ...overview, confirm: false });
                            setOverviewEndMonth(month);
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* ===== ACTION BUTTONS ===== */}
                  <div className="flex gap-2 mt-8 w-full">
                    <WhiteButton
                      name="Cancel"
                      className="hover:bg-black hover:text-white bg-white opacity-100 w-full text-sm py-4"
                      handleClick={() => {
                        setCustomBtn(false);
                        setOverview({ ...overview, confirm: true });
                        setOverviewStartMonth("");
                        setOverviewEndMonth("");
                        setOverviewStartYear("");
                        setOverviewEndYear("");
                        if (
                          customDates.endDate === "" &&
                          customDates.startDate === ""
                        ) {
                          setOverview({ ...overview, type: "monthly" });
                        }
                      }}
                    />

                    <DarkBlueButton
                      name="Choose"
                      className="w-full text-sm py-4"
                      handleClick={() => {
                        if (
                          overviewStartMonth &&
                          overviewEndMonth &&
                          overviewStartYear &&
                          overviewEndYear
                        ) {
                          const start = new Date(
                            `${overviewStartYear}-${
                              monthNames.indexOf(overviewStartMonth) + 1
                            }-01`
                          );
                          const end = new Date(
                            `${overviewEndYear}-${
                              monthNames.indexOf(overviewEndMonth) + 1
                            }-01`
                          );

                          if (start <= end) {
                            setOverview({ confirm: true, type: "custom" });
                            setCustomDates(
                              createCustomDateRange(
                                overviewStartMonth,
                                overviewEndMonth,
                                overviewStartYear,
                                overviewEndYear
                              )
                            );
                            setCustomBtn(false);
                          } else {
                            toast.error("Invalid Range");
                          }
                        } else {
                          toast.error(
                            "Please select year and month for both start and end."
                          );
                        }
                      }}
                    />
                  </div>
                </div>
              )}

              {!customBtn && (
                <LineGraph
                  type={overview.type}
                  customDates={customDates}
                  doctor_id={userData.doctor_id}
                />
              )}
            </div>
            {userData.role !== "Doctor" && (
              <div className=" w-full lg:w-1/2 bg-white rounded-lg px-4 py-5 lg:px-5 lg:py-6 pb-2 lg:pb-2">
                <div className="flex flex-col md:flex-row md:justify-between pl-4  items-center lg:items-start">
                  <h1 className="font-semibold text-xl">No shows overview</h1>
                  <div className="flex gap-2 mt-4 lg:mt-0 w-full md:w-auto">
                    <WhiteButton
                      name="Monthly"
                      className={
                        noshow === "month"
                          ? "bg-black hover:bg-black text-white  opacity-100 w-full md:w-20 !px-0"
                          : "hover:bg-black hover:text-white  bg-white opacity-100  w-full md:w-20 !px-0"
                      }
                      handleClick={() => {
                        setNoShow("month");
                      }}
                    />
                    <WhiteButton
                      name="Daily"
                      className={
                        noshow === "daily"
                          ? "bg-black hover:bg-black text-white  opacity-100 w-full md:w-20 !px-0"
                          : "hover:bg-black hover:text-white  bg-white opacity-100  w-full md:w-20 !px-0"
                      }
                      handleClick={() => {
                        setNoShow("daily");
                      }}
                    />
                  </div>
                </div>
                <BarGraph type={noshow} doctor_id={userData.doctor_id} />
              </div>
            )}
          </div>
        </>
      )}
      {(load || userData === undefined) && <Loader hospitalId={hospitalID} />}
    </div>
  );
};

export default Dashboard;
